import { Component, OnInit, HostBinding, ViewChild, Renderer2, ElementRef, AfterViewInit, OnDestroy, Injector } from '@angular/core';
import { SharedService } from '../services/shared.service';
import { HomeService } from '../services/home.service';
import { Constants } from '../constants';
import { ModuleNameEnum, Piwiktrack } from '../shared/piwiktrack';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { mvnfbuAnimations } from '../shared/animations';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { BackgroundLocalComponent } from '../shared/background-local/background-local.component';
import { VeNewsFeed, AllNewsResult } from '../typings';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter, take, switchMap } from 'rxjs/operators';
import { ActivityLoggerService } from '../services/activityLogger.service';
import { VideoStallHandlerService } from '../services/video-stall-handler.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ImageUtilsService } from '../services/image-utils.service';

// Constantes de timing
const ANIMATION_VIDEO_DELAY = 1000;
const VIDEO_METADATA_DELAY = 5000;
const VIDEO_FALLBACK_TIMEOUT = 8000;
const NEWS_DISPLAY_TIMING = 4000;
const BASE_DISPLAY_DURATION = 16000;
const SAFETY_ICON_ADDITIONAL_TIME = 3000;
const DOUBLE_TAP_DELAY = 300;
const TRANSITION_DELAY = 50;
const ANIMATION_DURATION = 1000;
const MIN_ANIMATION_DELAY = 2000;

@Component({
  selector: 'app-big-home',
  templateUrl: './big-home.component.html',
  styleUrls: ['./big-home.component.scss'],
  standalone: false,
  animations: [
    ...mvnfbuAnimations,
    trigger('newsCardAnimation', [
      state('up', style({
        transform: 'translateY(0)'
      })),
      state('down', style({
        transform: 'translateY(30.6vh)'
      })),
      transition('void => up', [
        style({ transform: 'translateY(30.6vh)' }),
        animate('1s ease-out', style({ transform: 'translateY(0)' }))
      ]),
      transition('down => up', [
        animate('1s ease-out')
      ]),
      transition('up => down', [
        animate('1s ease-in')
      ])
    ]),
    trigger('imageContainerSlide', [
      state('static', style({
        transform: 'translateX(0)'
      })),
      state('moved', style({
        transform: 'translateX(-100vw)'
      })),
      transition('static => moved', [
        animate('{{ duration }}ms {{ delay }}ms {{ easing }}')
      ], { params: { duration: 1000, delay: 8000, easing: 'ease-out' } })
    ])
  ]
})
export class BigHomeComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'fullWith';
  
  @ViewChild(BackgroundLocalComponent) background: BackgroundLocalComponent;

  
  public blobUrl = '';
  public withOtherNews = false;
  public newsFeeds: VeNewsFeed[] = [];
  public currentNewsIndex: number = 0;
  public currentNews: VeNewsFeed | undefined; 
  public futureNews?: VeNewsFeed; 
  public videoIsPlaying = false;
  public count = 0;
  public videoReady = false;
  public moveToTheLeft: boolean = false;
  public safetyIconUrl: string = '';
  public currentLanguage: string = '';
  private lastTap = 0;
  private doubleTapDelay = DOUBLE_TAP_DELAY;
  private newsDisplayTimeoutId?: number;
  public startingAnimation: boolean = true;
  public showNewsCardText: boolean = false;
  public animTabs: boolean = false;
  public zoomIn: boolean = false;
  public displayVideo: boolean = false;
  public animationKey: number = 0;
  public imageContainerState: 'static' | 'moved' = 'static';
  public imageContainerAnimationParams = { 
    duration: 1000, 
    delay: 8000, 
    easing: 'ease-out' 
  };
  private streamVideoTimeoutId?: number;
  private animationEndId?: number;
  private safetyIconTimeoutId?: number;
  private langChangeSubscription?: Subscription;
  private videoElement?: HTMLVideoElement;
  private videoEndedHandler?: () => void;
  private videoLoadedDataHandler?: () => void;
  private metadataTimeoutId: number | undefined;
  private fallbackTimeout: number | undefined;
  private destroy$ = new Subject<void>();
  private reloadTrigger$ = new Subject<void>();
  private prefetchedNews: AllNewsResult | null = null;
  private prefetchSubscription?: Subscription;
  animOut: boolean = true;
  
  // Portrait mode detection
  public isFirstImagePortrait: boolean = false;
  public isSecondImagePortrait: boolean = false;
  public staticFirstImageUrl: string = '';

  
  

  // ==================== LIFECYCLE HOOKS ====================
  
  constructor(public sharedService: SharedService, private homeService: HomeService, private translate: TranslateService, private router : Router, private el: ElementRef, private renderer:Renderer2, private logger: ActivityLoggerService, private injector: Injector, private authService: AuthService, private videoStallHandlerService: VideoStallHandlerService, private imageUtils: ImageUtilsService) {
    this.blobUrl = sharedService.blobUrl;
  }

  ngOnInit() {
    this.initializeData();
     this.currentLanguage = this.translate.currentLang;
        this.langChangeSubscription = this.translate.onLangChange
          .subscribe((langChangeEvent: LangChangeEvent) => {
            this.currentLanguage = langChangeEvent.lang;
        });
  }

  ngAfterViewInit(){
    this.initializeBackground();
  }

  ngOnDestroy(): void {
    console.log("ngOnDestroy BigHomeComponent");
    
    // Unsubscribe
    this.destroy$.next();
    this.destroy$.complete();
    this.reloadTrigger$.complete();
    this.prefetchSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
    
    // Clear tous les timeouts
    this.clearAllTimeouts();
    
    // Nettoyage vidéo
    this.resetVideoState();
  }

  // ==================== MÉTHODES D'INITIALISATION ====================

  public initializeData() {
    this.reloadTrigger$.pipe(
      switchMap(() => this.homeService.reloadNewsData()),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (elt) => this.applyReloadedNews(elt),
      error: (e: any) => {
        console.error(e);
        this.sharedService.navigate([Constants.RedirectUrl.Error]);
      }
    });

    const needsReload = this.homeService.getNeedToReloadNews();
    this.homeService.setNeedToReloadNews(false);

    // allNews est un BehaviorSubject : si des données sont déjà en cache, take(1) recevrait
    // immédiatement l'ancienne valeur avant la fin du loadNews() asynchrone. On passe donc
    // par reloadTrigger$ (Observable frais, sans valeur rejouée) pour un rechargement forcé.
    if (needsReload && this.homeService.allNews.getValue() !== null) {
      this.reloadTrigger$.next();
    } else {
      if (this.homeService.allNews.getValue() === null) {
        this.homeService.loadNews();
      }

      // Abonnement unique (take(1)) : cas du chargement initial, allNews n'a pas encore de valeur.
      this.homeService.getNews().pipe(
        filter((news): news is AllNewsResult => news !== null),
        take(1),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (news) => {
          console.log('Initializing local news data');
          if (news.internalNews && news.internalNews.length > 0) {
            this.newsFeeds = news.internalNews;
            this.currentNewsIndex = this.homeService.getNewsIndex();
            this.currentNews = this.newsFeeds[this.currentNewsIndex];
            this.futureNews = this.newsFeeds[this.currentNewsIndex];
            this.initializeBackground();
            this.displayCurrentNews(true);
            this.tryPrefetchForLastNews();
          } else if (news.localNews && news.localNews.length > 0) {
            console.warn('No News available, redirecting to Local.');
            this.sharedService.navigate([Constants.RedirectUrl.BigLocalNews]);
          }
          this.withOtherNews = (news.internalNews && news.internalNews.length > 0) ?? false;
        },
        error: (e: any) => {
          console.error(e);
          this.sharedService.navigate([Constants.RedirectUrl.Error]);
        }
      });
    }

    this.sharedService.getToken().then(token => {
      Piwiktrack.piwikTrackPage(this.homeService.uuid, ModuleNameEnum.ScreenView, token);
    });
  }

  // Affiche la news courante : ratio d'image, background et timer d'affichage.
  // isInitial : true pour un premier affichage/rechargement (setInitialValue), false pour une transition (changeValue).
  private displayCurrentNews(isInitial: boolean): void {
    this.showNewsCardText = true;
    this.animationKey++;
    this.zoomIn = true;
    this.animTabs = true;
    this.setTimerToDisplayChange();
    this.checkImageRatio(this.getFirstImage(this.currentNews), false);
    this.imageUtils.extractStaticBackground(this.getFirstImage(this.currentNews)).then(url => {
      this.staticFirstImageUrl = url;
      if (this.background) {
        if (isInitial) {
          this.background.setInitialValue(url, '#000000');
        } else {
          this.background.changeValue(url, '#000000');
        }
      }
    });
    if (this.hasSecondImage()) {
      this.checkImageRatio(this.currentNews!.image[1], true);
    }
  }

  // Prefetch en arrière-plan dès l'affichage de la dernière news, pour une transition instantanée en fin de playlist.
  private tryPrefetchForLastNews(): void {
    const total = this.newsFeeds?.length ?? 0;
    if (total === 0 || this.currentNewsIndex !== total - 1) return;

    this.prefetchedNews = null;
    this.prefetchSubscription?.unsubscribe();
    this.prefetchSubscription = this.homeService.reloadNewsData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => { this.prefetchedNews = data; },
        error: () => { this.prefetchedNews = null; }
      });
  }

  // Applique les news rechargées (prefetch ou fallback via reloadTrigger$) et repart à l'index 0, sans navigation.
  private applyReloadedNews(elt: AllNewsResult): void {
    if (elt.internalNews && elt.internalNews.length > 0) {
      this.newsFeeds = elt.internalNews;
      this.withOtherNews = true;
      this.homeService.setNewsIndex(0);
      this.currentNewsIndex = 0;
      this.currentNews = this.newsFeeds[0];
      this.futureNews = this.newsFeeds[0];
      this.initializeBackground();
      this.displayCurrentNews(true);
      this.tryPrefetchForLastNews();
    } else if (elt.localNews && elt.localNews.length > 0) {
      this.sharedService.navigate([Constants.RedirectUrl.BigLocalNews]);
    } else {
      this.sharedService.navigate([Constants.RedirectUrl.Error]);
    }
  }

  private initializeBackground(): void {
    if(this.background)
    {
        this.background.setInitialValue(this.getFirstImage(this.currentNews), '#000000');
    }
  }

  // ==================== GESTION DES ÉVÉNEMENTS UTILISATEUR ====================

  public onTouchStart(event: TouchEvent): void {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTap;
    
    if (tapLength < this.doubleTapDelay && tapLength > 0) {
      event.preventDefault();
      this.handleDoubleTap();
    }
    
    this.lastTap = currentTime;
  }

  public onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
  }

  private handleDoubleTap(): void {
    this.handleShiftNews();
  }

  // ==================== NAVIGATION ENTRE LES NEWS ====================
// Méthode utilitaire pour l’index
private updateNewsIndex(): void {
  this.homeService.setNewsIndex((this.currentNewsIndex + 1) % this.newsFeeds.length);
  this.currentNewsIndex = this.homeService.getNewsIndex();
  this.currentNews = this.newsFeeds[this.currentNewsIndex];
}

// Méthode utilitaire pour la gestion locale
private handleLocalNews(): void {
  this.homeService.setNewsIndex((this.currentNewsIndex + 1) % this.newsFeeds?.length);
  if (this.homeService.getNewsIndex() === 0) {
    this.homeService.setNeedToReloadNews(true);
  }
  this.currentNewsIndex = this.homeService.getNewsIndex();
  this.currentNews = this.newsFeeds[this.currentNewsIndex];
  this.sharedService.navigateByDisplayMode('local');
}

  private moveToNextNews(): void {
    console.log("moveToNextNews");
    this.imageContainerState = 'static'; // Réinitialiser l'état
    this.zoomIn = false;
    this.resetVideoState();
    this.safetyIconUrl = '';

    // Détecter la fin de liste AVANT la mise à jour de l'index (wrap-around).
    // Utilisé pour déclencher la navigation de fin de cycle.
    const isLastNews = this.currentNewsIndex === this.newsFeeds.length - 1;

    this.updateNewsIndex();
    this.futureNews = this.newsFeeds[this.currentNewsIndex];
    this.tryPrefetchForLastNews();

    // Cas 1 : news locale intercalée (id === '0') → navigation vers local
    if (this.futureNews.id === '0') {
      this.showNewsCardText = false;
      this.animationKey++;
      this.futureNews = undefined;
      this.handleLocalNews();
      return;
    }

    // Cas 2 : fin de playlist complète (l'index vient de boucler à 0) → rechargement in-place
    if (isLastNews) {
      this.showNewsCardText = false;
      this.animationKey++;
      this.futureNews = undefined;
      if (this.prefetchedNews) {
        const data = this.prefetchedNews;
        this.prefetchedNews = null;
        this.prefetchSubscription?.unsubscribe();
        this.applyReloadedNews(data);
      } else {
        this.prefetchSubscription?.unsubscribe();
        this.reloadTrigger$.next();
      }
      return;
    }

    // Cas 3 : news suivante normale
    this.showNewsCardText = false;
    this.zoomIn = false;
    this.animTabs = false;
    this.currentNews = this.futureNews;
    setTimeout(() => {
      this.displayCurrentNews(false);
    }, TRANSITION_DELAY);
  }

  private handleShiftNews(): void {
   this.clearVideoTimeout();
   this.moveToNextNews();
  }

  /**
   * Calcule la durée d'affichage d'une news
   * @returns Durée en millisecondes (16s de base + 3s si icône de sécurité)
   */
  private getNewsDisplayDuration(): number {
    const additionalTime = this.currentNews?.subType ? SAFETY_ICON_ADDITIONAL_TIME : 0;
    return BASE_DISPLAY_DURATION + additionalTime;
  }

  /**
   * Détermine si la news actuelle utilise une animation (et non une deuxième image)
   */
  public hasSecondImage(): boolean {
    return (this.currentNews?.image && this.currentNews.image.length > 1) || false;
  }

  // ==================== UTILITAIRES ====================

  /**
   * Récupère la première image disponible pour une news
   * Priorité : propriété 'image', sinon première image de la collection 'images'
   * @param news La news dont on veut récupérer l'image
   * @returns L'URL de la première image disponible, ou une chaîne vide si aucune image n'est disponible
   */
  public getFirstImage(news: VeNewsFeed | undefined): string {
    if (!news) {
      return '';
    }
    
    // Priorité 1: propriété image
    if (news.image[0]) {
      return news.image[0];
    }
    return '';
  }


  // ==================== NETTOYAGE ET TIMEOUTS ====================

  private cleanupVideoListeners(): void {
    this.videoStallHandlerService.cleanupVideoElement(
      this.videoElement, this.videoEndedHandler, this.videoLoadedDataHandler
    );
    this.videoElement = undefined;
    this.videoEndedHandler = undefined;
    this.videoLoadedDataHandler = undefined;
  }

  // ==================== GESTION DES ANIMATIONS ====================
  
  private setTimerToDisplayChange(): void {
    const displayDuration = this.getNewsDisplayDuration();
    // Le delay de l'animation = displayDuration - timing - 1000ms (durée de l'animation elle-même)
    const animationDelay = Math.max(displayDuration - NEWS_DISPLAY_TIMING - ANIMATION_DURATION, MIN_ANIMATION_DELAY);
    // Mettre à jour les params d'animation
    this.imageContainerAnimationParams = {
      duration: ANIMATION_DURATION,
      delay: animationDelay,
      easing: 'ease-out'
    };
    
    this.clearDisplayChangeTimeout();
    this.newsDisplayTimeoutId = window.setTimeout(() => {
      console.log("Timer ended - changing news display");
      this.showNewsCardText = false;
      this.animationKey++; // Forcer le redéclenchement de l'animation
      if(this.currentNews && this.currentNews.videoUrl !== null){
        if(this.sharedService.isYoutubeVideo(this.currentNews)){
          this.displayVideo = true;
        }
        else
         this.displayStreamVideo();
        }
      else{
        this.moveToTheLeft = true;
        this.imageContainerState = 'moved'; // Déclencher l'animation Angular
        this.animOut = false;
      }
    }, NEWS_DISPLAY_TIMING);

    // Le timeout final est basé sur displayDuration
    if(!this.currentNews?.videoUrl){
      this.animationEndId = window.setTimeout(() => {
        this.moveToTheLeft = false;
        this.moveToNextNews();      
      }, displayDuration);
    }
    
  }

  // ==================== CLEANUP METHODS ====================

  /**
   * Réinitialise complètement l'état de la vidéo
   * Nettoie les timeouts, listeners et flags
   */
  private resetVideoState(): void {
    this.clearVideoTimeout(); // inclut cleanupVideoListeners()
    this.clearMetadataTimeout();
    this.videoReady = false;
    this.videoIsPlaying = false;
    this.displayVideo = false;
  }

  /** clearTimeout(undefined) est un no-op natif — pas besoin de guard if. */
  private clearTimer(id: number | undefined): undefined {
    window.clearTimeout(id);
    return undefined;
  }

  private clearAllTimeouts(): void {
    this.clearDisplayChangeTimeout();
    this.clearVideoTimeout();
    this.clearMetadataTimeout();
    this.clearSafetyIconTimeout();
  }

  private clearVideoTimeout(): void {
    this.streamVideoTimeoutId = this.clearTimer(this.streamVideoTimeoutId);
    this.fallbackTimeout      = this.clearTimer(this.fallbackTimeout);
    this.animationEndId       = this.clearTimer(this.animationEndId);
    this.cleanupVideoListeners();
  }

  private clearMetadataTimeout(): void {
    this.metadataTimeoutId = this.clearTimer(this.metadataTimeoutId);
  }

  private clearDisplayChangeTimeout(): void {
    this.newsDisplayTimeoutId = this.clearTimer(this.newsDisplayTimeoutId);
  }

  private clearSafetyIconTimeout(): void {
    this.safetyIconTimeoutId = this.clearTimer(this.safetyIconTimeoutId);
  }

  getSafetyTypeIcon(arg0: string|null) {
    return this.safetyIconUrl;
  }

  // ==================== DETECTION DU RATIO D'IMAGE ====================
  
  /**
   * Détecte le ratio d'une image pour déterminer si elle est en portrait
   * @param imageUrl URL de l'image à analyser
   * @param isSecondImage true si c'est la deuxième image de la news
   */
  public checkImageRatio(imageUrl: string, isSecondImage: boolean = false): void {
    if (!imageUrl) return;
    
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const isPortrait = aspectRatio <= 1.2; // Portrait ou carré
      console.log(`Image loaded: ${imageUrl}, aspectRatio: ${aspectRatio.toFixed(2)}, isPortrait: ${isPortrait}`);
      if (isSecondImage) {
        this.isSecondImagePortrait = isPortrait;
      } else {
        this.isFirstImagePortrait = isPortrait;
      }
    };
    
    img.onerror = (err) => {
      console.error('Image load failed:', imageUrl, err);
      // Définir un comportement par défaut
      if (isSecondImage) {
        this.isSecondImagePortrait = false;
      } else {
        this.isFirstImagePortrait = false;
      }
    };
    
    img.src = imageUrl;
  }

    // ==================== GESTION DES VIDÉOS ====================

  /**
   * Lance la lecture d'une vidéo locale après vérification de l'état de disponibilité.
   * Flux : displayStreamVideo → onVideoMetadataLoaded → playVideoIfReady
   * Timeouts : 
   * - ANIMATION_VIDEO_DELAY (1000ms) avant setup
   * - VIDEO_METADATA_DELAY (5000ms) avant marquage ready
   * - VIDEO_FALLBACK_TIMEOUT (8000ms) timeout de sécurité
   */
  public displayStreamVideo(): void {
    console.log('=== displayStreamVideo called ===');
    console.log('Current news has video:', this.currentNews?.video);
    console.log('videoIsPlaying:', this.videoIsPlaying);
    
    // Nettoyer TOUT : timeout en cours ET listeners existants
    this.clearVideoTimeout();

    this.streamVideoTimeoutId = window.setTimeout(() => {
      this.setupVideoPlayback();
    }, ANIMATION_VIDEO_DELAY);
  }

  /**
   * Configure l'élément vidéo et les listeners nécessaires
   */
  private setupVideoPlayback(): void {
    console.log('=== Video timeout executed (after ANIMATION_VIDEO_DELAY) ===');
    this.videoElement = document.getElementById('videoLocal') as HTMLVideoElement;
    console.log('Video element found:', !!this.videoElement);
    
    if (!this.videoElement || this.videoIsPlaying) {
      console.warn('Cannot setup video:', {
        elementExists: !!this.videoElement,
        alreadyPlaying: this.videoIsPlaying
      });
      return;
    }
    
    console.log('Video element source:', this.videoElement.src);
    console.log('Video element readyState:', this.videoElement.readyState);
    
    // Réinitialiser le temps de lecture
    this.videoElement.currentTime = 0;
    
    // Add error handler for video loading errors
    this.videoElement.onerror = (e) => this.handleVideoError(e);
    
    // Fallback: if metadata doesn't load within timeout, skip video
    this.fallbackTimeout = window.setTimeout(() => {
      if (!this.videoReady) {
        console.warn('Video metadata timeout - skipping video');
        console.warn('Video source:', this.videoElement?.src);
        console.warn('Video readyState:', this.videoElement?.readyState);
        this.moveToNextNews();
      }
    }, VIDEO_FALLBACK_TIMEOUT);
    
    // Handler canplay : déclenche dès qu'il y a assez de données pour jouer
    this.videoLoadedDataHandler = () => {
      console.log('=== canplay event fired ===');
      this.playVideoIfReady();
    };
    
    console.log('Adding canplay listener...');
    // Ajouter le listener pour canplay
    this.videoElement.addEventListener('canplay', this.videoLoadedDataHandler, { once: true });
  }

  /**
   * Gère les erreurs de chargement vidéo
   */
  private handleVideoError(e: Event | string): void {
    console.error('Video load error:', e);
    console.error('Video source:', this.videoElement?.src);
    this.logger.logError('Video load error in BigHomeComponent', e.toString());
    this.moveToNextNews(); // Skip to next news on error
  }

  /**
   * Callback déclenché quand les métadonnées de la vidéo sont chargées
   * Attend VIDEO_METADATA_DELAY avant de marquer la vidéo comme prête
   */
  public onVideoMetadataLoaded(): void {
    this.videoElement = document.getElementById('videoLocal') as HTMLVideoElement;
    console.log('=== Video metadata loaded event fired ===', this.videoElement);
    console.log('Video source:', this.videoElement?.src);
    console.log('Video readyState:', this.videoElement?.readyState);
    
    // Attendre VIDEO_METADATA_DELAY après le chargement des métadonnées
    this.clearMetadataTimeout();
    this.metadataTimeoutId = window.setTimeout(() => {
      this.videoReady = true;
      console.log('=== Video marked as ready after metadata timeout ===');
      this.playVideoIfReady();
    }, VIDEO_METADATA_DELAY);
  }

  /**
   * Lance la lecture de la vidéo si elle est prête
   * Gère les erreurs de lecture et configure le handler de fin de vidéo
   */
  private playVideoIfReady(): void {
    this.videoElement = document.getElementById('videoLocal') as HTMLVideoElement;
    
    if (!this.videoElement || !this.videoReady || this.videoIsPlaying) {
      return;
    }
    
    if (this.fallbackTimeout) {
      window.clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = undefined;
    }
    
    // Créer et sauvegarder le handler ended
    this.videoEndedHandler = () => {
      console.log("Video ended, moving to next news");
      this.moveToNextNews();
    };

    this.videoElement.addEventListener('ended', this.videoEndedHandler);
    this.videoIsPlaying = true;
    this.videoStallHandlerService.attachStallHandlers(this.videoElement, () => this.moveToNextNews());
    console.log("Video ready, playing...");
    this.videoElement.muted = true;
    
    this.videoElement.play()
      .catch(err => {
        console.error('Error playing video:', err);
        this.logger.logError('Video play failed', err.message);
        this.moveToNextNews(); // Fallback en cas d'échec
      });
    this.displayVideo = true;
  }

    public youtubeClose(close: boolean) {
      this.moveToNextNews();
     }
}